// @ts-strict-ignore
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectAllAppsIdsInOrg,
    selectAppsById,
    selectShouldShowAutomationsTimelineEventsOnly,
    selectTimelineEventsFilters,
} from '../../selectors/selectors';
import { selectAvailableDestinations, selectDestinationsData } from '@/selectors/summaryViewSelectors';
import { useGetOrgTimelineEventsQuery, useLazyGetOrgTimelineEventsQuery } from '../../middlewares/paginationApi';
import { globalMessage } from '@/utils/globalMessage';
import { Timeline } from './TimelineEventsContainer';
import { ReleaseTimelineSectionContext } from './EventsTimeline';
import Spin from 'antd/lib/spin';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PaginationInfo } from '@/actions/pagination';
import type { TimelineEvent } from '../../types';
import { uniq, compact } from 'lodash';
import dayjs from 'dayjs';
import { shallowEqual } from 'react-redux';
import { tryFetchEntity } from '@/actions/entities';
import { FetchableEntityType } from '@/entityType';

interface OrgTimelineEventsProps {
    orgId: string;
}

const OrgTimelineEvents: React.FC<OrgTimelineEventsProps> = ({ orgId }) => {
    const dispatch = useDispatch();
    const [allEvents, setAllEvents] = useState<TimelineEvent[]>([]);
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>(null);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(true);
    const timelineEventFilters = useSelector((state: RootState) => selectTimelineEventsFilters(state), shallowEqual);
    const shouldShowAutomationsOnly = useSelector((state: RootState) =>
        selectShouldShowAutomationsTimelineEventsOnly(state),
    );
    const appIds = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId), shallowEqual);
    const availableApps = useSelector((state: RootState) => selectAppsById(state, appIds), shallowEqual);
    const availableDestinations = useSelector(
        (state: RootState) => selectAvailableDestinations(state, orgId),
        shallowEqual,
    );
    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));

    useEffect(() => {
        if (!orgDestinationsData) {
            dispatch(tryFetchEntity(FetchableEntityType.OrgDestinationsAndVersions, { orgId }, orgId));
        }
    }, [dispatch, orgDestinationsData, orgId]);

    const baseRequest = useMemo(
        () => ({
            orgId,
            timelineEventFilters,
            shouldShowAutomationsTimelineEventsOnly: shouldShowAutomationsOnly,
            includeOrgTimelineFilters: true,
        }),
        [orgId, timelineEventFilters, shouldShowAutomationsOnly],
    );
    const initialQuery = useGetOrgTimelineEventsQuery(baseRequest);
    const [fetchMore, lazyResult] = useLazyGetOrgTimelineEventsQuery();

    const { currentData: initialData, isLoading } = initialQuery;
    const { data: moreData, isLoading: isLazyLoading } = lazyResult;

    useEffect(() => {
        setAllEvents([]);
        setPaginationInfo(null);
        setIsLoadingMore(false);
    }, [timelineEventFilters, shouldShowAutomationsOnly, orgId]);

    useEffect(() => {
        if (initialData?.data) {
            setAllEvents(initialData.data.events);
            setPaginationInfo(initialData.data.paginationInfo);
        }
        setSuccess(!initialData?.errorMessage?.length);
    }, [initialData]);

    useEffect(() => {
        if (moreData?.data) {
            const newEvents = moreData.data.events;
            const updatedEvents = sortCombinedEvents(allEvents, newEvents);
            setAllEvents(updatedEvents);
            setPaginationInfo(moreData.data.paginationInfo);
            setIsLoadingMore(false);
        }
        setSuccess(!moreData?.errorMessage?.length);
    }, [moreData]);

    const loadMore = useCallback(() => {
        if (!paginationInfo || isLoadingMore || !paginationInfo.hasMore) return;
        setIsLoadingMore(true);
        fetchMore({ ...baseRequest, nextCursor: paginationInfo.nextCursor });
    }, [paginationInfo, isLoadingMore, fetchMore, baseRequest]);

    useEffect(() => {
        if (!success) {
            globalMessage.error('An error occurred when fetching timeline events.');
        }
    }, [success]);

    const availableVersions = uniq(compact(allEvents.map((event) => event.releaseData?.displayableVersion)));

    if (isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100vh',
                    flexGrow: 1,
                }}
            >
                <Spin />
            </div>
        );
    }

    return (
        <Timeline
            releaseId={null}
            appId={null}
            orgId={orgId}
            events={allEvents}
            isDrawerOpen={false}
            loadMore={loadMore}
            isLoading={isLoadingMore || isLazyLoading}
            hasMore={paginationInfo?.hasMore}
            context={ReleaseTimelineSectionContext.OrgTimeline}
            availableVersions={availableVersions}
            availableApps={availableApps}
            availableDestinations={availableDestinations}
        />
    );
};

const sortCombinedEvents = (allEvents: TimelineEvent[], newEvents: TimelineEvent[]) => {
    const updatedEvents = [...allEvents, ...newEvents];

    if (newEvents.length && allEvents.length) {
        const newestDate = dayjs(newEvents[0].triggeredAt);
        const oldestDate = dayjs(allEvents[allEvents.length - 1].triggeredAt);

        if (newestDate.isAfter(oldestDate)) {
            updatedEvents.sort((a, b) => dayjs(b.triggeredAt).diff(a.triggeredAt));
        }
    }

    return updatedEvents;
};

export default OrgTimelineEvents;
